// @ts-nocheck
import { useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { arrayMove } from "@dnd-kit/sortable";
import { Icon } from "@/components/Icon";
import { iconNames } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { KiiBadge } from "@/components/kii/kii-badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PhotoManagementDialog } from "@/components/kii/kii-photo-management-dialog";
import { useDebug } from "@/contexts/DebugContext";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { cn } from "@/lib/utils";
import type { TranscriptJSON, TranscriptObservation, DocumentAttachment } from "@/shared/schema";
import type { FileUploadProgress } from "@/hooks/useFileUpload";

// Simple API request helper
async function apiRequest(method: string, url: string, data?: unknown): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`${res.status}: ${res.statusText}`);
  }
  return res;
}

// NM photo data from NaraMachine image_files
export interface NMPhoto {
  id: string;
  filename: string;
  url?: string;
  mime_type?: string;
  size?: number;
  metadata?: {
    exif?: {
      DateTime?: string;
      DateTimeOriginal?: string;
    };
  };
}

// Component to display NM photos with signed URL fetching
function NMPhotoImage({ gcsUrl, className }: { gcsUrl: string; className?: string }) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!gcsUrl) {
      setIsLoading(false);
      setError("No URL");
      return;
    }

    const fetchSignedUrl = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const res = await apiRequest("POST", "/api/nm-files/signed-url", { gcsUrl });
        const data = await res.json();
        setSignedUrl(data.url);
      } catch (err) {
        setError("Failed to load image");
      } finally {
        setIsLoading(false);
      }
    };

    fetchSignedUrl();
  }, [gcsUrl]);

  if (isLoading) {
    return <Skeleton className={cn("kii-editor-image", className)} />;
  }

  if (error || !signedUrl) {
    return (
      <div className={cn("kii-editor-image bg-muted flex items-center justify-center", className)}>
        <Icon name={iconNames.image} size={24} className="text-muted-foreground" />
      </div>
    );
  }

  return (
    <img
      src={signedUrl}
      alt="Photo"
      className={cn("kii-editor-image", className)}
    />
  );
}

// Editable photo component with zoom, remove, move, and rotate controls
interface EditablePhotoProps {
  photoUrl: string;
  photoId: string;
  alt: string;
  isNMDocument?: boolean;
  isPreSignedUrl?: boolean;
  photoIndex: number;
  totalPhotos: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onRemove: (photoId: string) => void;
  onMoveUp: (photoId: string) => void;
  onMoveDown: (photoId: string) => void;
  onRotate?: (photoId: string, angle: 90 | 180 | 270) => void;
  pendingRotations?: Record<string, number>;
  onClearPendingRotation?: (photoId: string) => void;
  processingPhotoIds?: string[];
}

function EditablePhoto({
  photoUrl,
  photoId,
  alt,
  isNMDocument = false,
  isPreSignedUrl = false,
  photoIndex,
  totalPhotos,
  canMoveUp,
  canMoveDown,
  onRemove,
  onMoveUp,
  onMoveDown,
  onRotate,
  pendingRotations,
  onClearPendingRotation,
  processingPhotoIds,
}: EditablePhotoProps) {
  const pendingRotation = pendingRotations?.[photoId] ?? 0;
  const isProcessing = processingPhotoIds?.includes(photoId) ?? false;

  const [baseUrl, setBaseUrl] = useState<string | undefined>();
  const [preloadingUrl, setPreloadingUrl] = useState<string | null>(null);

  const [isZoomed, setIsZoomed] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOverlayOpen) return;

    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOverlayOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOverlayOpen]);

  const handleImageClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

    if (isTouchDevice) {
      if (isOverlayOpen) {
        setIsZoomed(true);
      } else {
        setIsOverlayOpen(true);
      }
    } else {
      setIsZoomed(true);
    }
  };

  const needsSignedUrlFetch = isNMDocument && !isPreSignedUrl && !!photoUrl;

  const { data: signedUrl, isLoading, error } = useQuery({
    queryKey: ['/api/nm-files/signed-url', photoUrl],
    queryFn: async () => {
      const res = await apiRequest("POST", "/api/nm-files/signed-url", { gcsUrl: photoUrl });
      const data = await res.json();
      return data.url as string;
    },
    enabled: needsSignedUrlFetch,
    staleTime: 1000 * 60 * 30,
  });

  const displayUrl = isPreSignedUrl ? photoUrl : (isNMDocument ? signedUrl : photoUrl);

  // Track displayUrl changes — preload during processing, swap directly otherwise
  useEffect(() => {
    if (!displayUrl) return;
    if (isProcessing || pendingRotation > 0) {
      if (!baseUrl) {
        setBaseUrl(displayUrl);
      } else if (displayUrl !== baseUrl) {
        setPreloadingUrl(displayUrl);
      }
    } else {
      setBaseUrl(displayUrl);
      setPreloadingUrl(null);
    }
  }, [displayUrl]);

  // CSS angle: backend uses CCW convention, CSS rotate() is CW — always go negative to avoid direction reversal
  const cssAngle = -pendingRotation;
  const rotateStyle: React.CSSProperties | undefined = pendingRotation > 0
    ? { transform: `rotate(${cssAngle}deg)`, transition: 'transform 0.15s ease' }
    : undefined;

  // Prevent double-firing when both thumbnail and zoom overlays load
  const overlayHandledRef = useRef(false);
  useEffect(() => {
    if (preloadingUrl) overlayHandledRef.current = false;
  }, [preloadingUrl]);

  const handleOverlayLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    // Make overlay visible — it covers the CSS-rotated image beneath, preventing flash
    (e.target as HTMLImageElement).style.visibility = 'visible';
    if (!overlayHandledRef.current && preloadingUrl) {
      overlayHandledRef.current = true;
      setBaseUrl(preloadingUrl);
      setPreloadingUrl(null);
      onClearPendingRotation?.(photoId);
    }
  };

  const handleRemove = () => {
    if (isRemoving) return;
    setIsRemoving(true);
    setIsZoomed(false);
    setShowRemoveConfirm(false);
    onRemove(photoId);
  };

  const handleRemoveClick = () => {
    setShowRemoveConfirm(true);
  };

  if (needsSignedUrlFetch && isLoading) {
    return <Skeleton className="kii-editor-image aspect-square" />;
  }

  if (needsSignedUrlFetch && (error || !displayUrl)) {
    return (
      <div className="kii-editor-image bg-muted flex items-center justify-center aspect-square">
        <Icon name={iconNames.image} size={24} className="text-muted-foreground" />
      </div>
    );
  }

  if (!displayUrl) {
    return null;
  }

  const showControls = isOverlayOpen;

  return (
    <>
      <div
        ref={containerRef}
        className={cn("kii-editor-photo-hover-area", showControls && "is-active")}
        onMouseEnter={() => setIsOverlayOpen(true)}
        onMouseLeave={() => setIsOverlayOpen(false)}
      >
        <div className="kii-editor-photo-card">
          <div className="kii-editor-image-wrapper overflow-hidden">
            <img
              src={baseUrl}
              alt={alt}
              className="kii-editor-image cursor-pointer"
              style={rotateStyle}
              onClick={handleImageClick}
              data-testid={`editable-photo-${photoId}`}
            />
            {preloadingUrl && (
              <img
                src={preloadingUrl}
                className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                style={{ visibility: 'hidden' }}
                onLoad={handleOverlayLoad}
                onError={handleOverlayLoad}
                alt=""
              />
            )}
            {isProcessing && (
              <div className="absolute bottom-1 right-1 bg-background/70 rounded-full p-0.5">
                <Icon name={iconNames.sync} size={14} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {(canMoveUp || canMoveDown) && (
            <div className={cn("kii-editor-photo-controls-right transition-opacity", showControls ? "opacity-100" : "opacity-0 pointer-events-none")}>
              {canMoveUp && (
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7 rounded-md shadow-md bg-card"
                  onClick={(e) => { e.stopPropagation(); onMoveUp(photoId); }}
                  title="Move photo up"
                  data-testid={`button-move-photo-up-${photoId}`}
                >
                  <Icon name={iconNames.chevronUp} size={16} />
                </Button>
              )}
              {canMoveDown && (
                <Button
                  variant="secondary"
                  size="icon"
                  className="h-7 w-7 rounded-md shadow-md bg-card"
                  onClick={(e) => { e.stopPropagation(); onMoveDown(photoId); }}
                  title="Move photo down"
                  data-testid={`button-move-photo-down-${photoId}`}
                >
                  <Icon name={iconNames.chevronDown} size={16} />
                </Button>
              )}
            </div>
          )}

          <div className={cn("kii-editor-photo-controls-bottom transition-opacity", showControls ? "opacity-100" : "opacity-0 pointer-events-none")}>
            {onRotate && (
              <Button
                variant="secondary"
                size="icon"
                className="h-7 w-7 rounded-md shadow-md bg-card"
                onClick={(e) => { e.stopPropagation(); onRotate(photoId, 90); }}
                title="Rotate left"
                data-testid={`button-rotate-left-${photoId}`}
              >
                <Icon name={iconNames.undo} size={16} />
              </Button>
            )}
            <Button
              variant="destructive"
              size="icon"
              className="h-7 w-7 rounded-md shadow-md"
              onClick={(e) => { e.stopPropagation(); handleRemoveClick(); }}
              title="Remove"
              data-testid={`button-remove-${photoId}`}
            >
              <Icon name={iconNames.delete} size={16} />
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={isZoomed} onOpenChange={setIsZoomed}>
        <DialogContent className="max-w-4xl p-4">
          <DialogHeader className="sr-only">
            <DialogTitle>{alt}</DialogTitle>
          </DialogHeader>
          <div className="relative overflow-hidden max-h-[80vh] w-full flex items-center justify-center">
            <div className="relative">
              <img
                src={baseUrl}
                alt={alt}
                className="max-w-full max-h-[80vh] object-contain"
                style={rotateStyle}
                data-testid={`zoomed-photo-${photoId}`}
              />
              {preloadingUrl && (
                <img
                  src={preloadingUrl}
                  className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                  style={{ visibility: 'hidden' }}
                  onLoad={handleOverlayLoad}
                  onError={handleOverlayLoad}
                  alt=""
                />
              )}
            </div>
            {isProcessing && (
              <div className="absolute bottom-2 right-2 bg-background/70 rounded-full p-1">
                <Icon name={iconNames.sync} size={18} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-2 mt-2">
            {onRotate && (
              <Button
                variant="secondary"
                size="icon"
                className="h-8 w-8 rounded-md"
                onClick={() => onRotate(photoId, 90)}
                title="Rotate left"
              >
                <Icon name={iconNames.undo} size={18} />
              </Button>
            )}
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8 rounded-md"
              onClick={() => { setIsZoomed(false); handleRemoveClick(); }}
              title="Remove"
            >
              <Icon name={iconNames.delete} size={18} />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Photo</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this photo from the observation?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemove} className="bg-destructive text-destructive-foreground">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

interface KiiEditorObservationProps {
  observation: TranscriptObservation;
  index: number;
  totalObservations: number;
  attachments: DocumentAttachment[];
  nmPhotos?: NMPhoto[];
  isNMDocument?: boolean;
  onUpdateTitle: (index: number, newTitle: string) => void;
  onUpdateDescription: (index: number, newDescription: string) => void;
  onDeleteObservation: (index: number) => void;
  onMoveObservationUp: (index: number) => void;
  onMoveObservationDown: (index: number) => void;
  onRemovePhoto: (observationIndex: number, photoId: string) => void;
  onMovePhotoUp: (observationIndex: number, photoId: string) => void;
  onMovePhotoDown: (observationIndex: number, photoId: string) => void;
  onMergeWithPrevious: (index: number) => void;
  onRotatePhoto?: (photoId: string, angle: 90 | 180 | 270) => void;
  pendingRotations?: Record<string, number>;
  onClearPendingRotation?: (photoId: string) => void;
  processingPhotoIds?: string[];
  onOpenPhotoManager?: (observationIndex: number) => void;
}

function ObservationCard({
  observation,
  index,
  totalObservations,
  attachments,
  nmPhotos = [],
  isNMDocument = false,
  onUpdateTitle,
  onUpdateDescription,
  onDeleteObservation,
  onMoveObservationUp,
  onMoveObservationDown,
  onRemovePhoto,
  onMovePhotoUp,
  onMovePhotoDown,
  onMergeWithPrevious,
  onRotatePhoto,
  pendingRotations,
  onClearPendingRotation,
  processingPhotoIds,
  onOpenPhotoManager,
}: KiiEditorObservationProps) {
  const { isDebugEnabled } = useDebug();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isFirstObservation = index === 0;
  const isLastObservation = index === totalObservations - 1;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [observation.description]);

  const getLocalPhotoUrl = (photoId: string): string | null => {
    const attachment = attachments.find(a => a.id === photoId);
    if (attachment) {
      return `/api/attachments/${attachment.id}/oriented`;
    }
    return null;
  };

  const getNMPhotoUrl = (photoId: string): { url: string; isPreSigned: boolean } | null => {
    const photo = nmPhotos.find(p => p.id === photoId);
    if (photo) {
      if (photo.url) {
        return { url: photo.url, isPreSigned: true };
      }
      if (photo.filename) {
        return { url: photo.filename, isPreSigned: false };
      }
    }
    return null;
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderPhotos = () => {
    const photoIds = observation.photo_ids || [];
    const hasPhotos = photoIds.length > 0;
    const hasManyImages = photoIds.length > 2;
    const containerClass = cn(
      "kii-editor-image-container",
      hasManyImages && "has-many-images"
    );
    const totalPhotos = photoIds.length;

    return (
      <div className={containerClass} data-testid={`images-${index}`}>
        {photoIds.map((photoId, photoIdx) => {
          const canMoveUp = photoIdx > 0 || !isFirstObservation;
          const canMoveDown = photoIdx < totalPhotos - 1 || !isLastObservation;

          if (isNMDocument) {
            const photoData = getNMPhotoUrl(photoId);
            if (!photoData) return null;
            return (
              <EditablePhoto
                key={photoId}
                photoUrl={photoData.url}
                photoId={photoId}
                alt={`${observation.title} - ${photoIdx + 1}`}
                isNMDocument={true}
                isPreSignedUrl={photoData.isPreSigned}
                photoIndex={photoIdx}
                totalPhotos={totalPhotos}
                canMoveUp={canMoveUp}
                canMoveDown={canMoveDown}
                onRemove={(id) => onRemovePhoto(index, id)}
                onMoveUp={(id) => onMovePhotoUp(index, id)}
                onMoveDown={(id) => onMovePhotoDown(index, id)}
                onRotate={onRotatePhoto}
                pendingRotations={pendingRotations}
                onClearPendingRotation={onClearPendingRotation}
                processingPhotoIds={processingPhotoIds}
              />
            );
          } else {
            const photoUrl = getLocalPhotoUrl(photoId);
            if (!photoUrl) return null;
            return (
              <EditablePhoto
                key={photoId}
                photoUrl={photoUrl}
                photoId={photoId}
                alt={`${observation.title} - ${photoIdx + 1}`}
                isNMDocument={false}
                isPreSignedUrl={false}
                photoIndex={photoIdx}
                totalPhotos={totalPhotos}
                canMoveUp={canMoveUp}
                canMoveDown={canMoveDown}
                onRemove={(id) => onRemovePhoto(index, id)}
                onMoveUp={(id) => onMovePhotoUp(index, id)}
                onMoveDown={(id) => onMovePhotoDown(index, id)}
              />
            );
          }
        })}
        {onOpenPhotoManager && (
          <button
            className="kii-editor-add-photo-tile"
            onClick={() => onOpenPhotoManager(index)}
            data-testid={`button-photo-manager-${index}`}
            title="Manage photos"
          >
            <Icon name={iconNames.add} size={hasPhotos ? 24 : 20} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="kii-editor-observation-wrapper">
      <div className="kii-editor-observation">
        <div className="kii-editor-observation-controls">
          {!isFirstObservation && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onMoveObservationUp(index)}
              title="Move up"
              data-testid={`button-move-obs-up-${index}`}
            >
              <Icon name={iconNames.chevronUp} size={20} />
            </Button>
          )}
          {!isLastObservation && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onMoveObservationDown(index)}
              title="Move down"
              data-testid={`button-move-obs-down-${index}`}
            >
              <Icon name={iconNames.chevronDown} size={20} />
            </Button>
          )}
        </div>
        <Card
          className="kii-editor-card"
          data-testid={`observation-${index}`}
        >
          <button
            onClick={() => onDeleteObservation(index)}
            className="kii-editor-delete-button"
            data-testid={`button-delete-observation-${index}`}
          >
            <Icon name={iconNames.delete} size={16} />
          </button>

          <div className="kii-editor-card-content">
            <div className="kii-editor-card-layout">
              {renderPhotos()}

              <div className="kii-editor-content-column">
                <div className="kii-editor-title-row">
                  <Input
                    value={observation.title}
                    onChange={(e) => onUpdateTitle(index, e.target.value)}
                    className="kii-editor-title-input"
                    placeholder="Observation title"
                    data-testid={`input-title-${index}`}
                  />
                </div>

                {isDebugEnabled && (
                  <div className="flex gap-2 flex-wrap">
                    <KiiBadge variant="outline" className="font-mono text-xs" data-testid={`time-${index}`}>
                      {formatTime(observation.start_time)} - {formatTime(observation.end_time)}
                    </KiiBadge>
                    <KiiBadge variant="outline" className="font-mono text-xs" data-testid={`photos-${index}`}>
                      {observation.photo_ids.length} photo(s)
                    </KiiBadge>
                  </div>
                )}

                <div className="kii-editor-description">
                  <Textarea
                    ref={textareaRef}
                    value={observation.description}
                    onChange={(e) => onUpdateDescription(index, e.target.value)}
                    className="p-2 m-0 bg-transparent border-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
                    style={{ backgroundColor: 'transparent', minHeight: '100px' }}
                    placeholder="Enter description..."
                    data-testid={`textarea-description-${index}`}
                  />
                </div>

              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export interface KiiEditorProps {
  transcriptData: TranscriptJSON;
  onTranscriptChange: (data: TranscriptJSON) => void;
  attachments?: DocumentAttachment[];
  nmPhotos?: NMPhoto[];
  isNMDocument?: boolean;
  isSaving?: boolean;
  lastSaved?: Date | null;
  hasChanges?: boolean;
  className?: string;
  onRotatePhoto?: (photoId: string, angle: 90 | 180 | 270) => void;
  pendingRotations?: Record<string, number>;
  onClearPendingRotation?: (photoId: string) => void;
  processingPhotoIds?: string[];
  onUndo?: () => void;
  canUndo?: boolean;
  onPermanentDeletePhoto?: (photoId: string) => Promise<void>;
  onUploadPhotos?: (files: File[]) => Promise<void>;
  uploadProgress?: FileUploadProgress[];
  isUploadingPhotos?: boolean;
}

export function KiiEditor({
  transcriptData,
  onTranscriptChange,
  attachments = [],
  nmPhotos = [],
  isNMDocument = false,
  isSaving = false,
  lastSaved = null,
  hasChanges = false,
  className,
  onRotatePhoto,
  pendingRotations,
  onClearPendingRotation,
  processingPhotoIds,
  onUndo,
  canUndo = false,
  onPermanentDeletePhoto,
  onUploadPhotos,
  uploadProgress = [],
  isUploadingPhotos = false,
}: KiiEditorProps) {
  const { isDebugEnabled } = useDebug();
  const { formatDate } = useDateFormatter();

  // Photo management dialog state
  const [photoManagerOpen, setPhotoManagerOpen] = useState(false);
  const [photoManagerObsIndex, setPhotoManagerObsIndex] = useState(0);

  // Compute unused photos: photos in nmPhotos but not referenced in any observation
  const usedPhotoIds = new Set(
    transcriptData.observations.flatMap(obs => obs.photo_ids)
  );
  const unusedPhotos = nmPhotos.filter(p => !usedPhotoIds.has(p.id));

  const handleUpdateTitle = (index: number, newTitle: string) => {
    onTranscriptChange({
      ...transcriptData,
      observations: transcriptData.observations.map((obs, idx) =>
        idx === index ? { ...obs, title: newTitle } : obs
      ),
    });
  };

  const handleUpdateDescription = (index: number, newDescription: string) => {
    onTranscriptChange({
      ...transcriptData,
      observations: transcriptData.observations.map((obs, idx) =>
        idx === index ? { ...obs, description: newDescription } : obs
      ),
    });
  };

  const handleDeleteObservation = (index: number) => {
    onTranscriptChange({
      ...transcriptData,
      observations: transcriptData.observations.filter((_, idx) => idx !== index),
    });
  };

  const handleMoveObservationUp = (index: number) => {
    if (index <= 0) return;
    onTranscriptChange({
      ...transcriptData,
      observations: arrayMove(transcriptData.observations, index, index - 1),
    });
  };

  const handleMoveObservationDown = (index: number) => {
    if (index >= transcriptData.observations.length - 1) return;
    onTranscriptChange({
      ...transcriptData,
      observations: arrayMove(transcriptData.observations, index, index + 1),
    });
  };

  const handleInsertObservation = (insertIndex: number) => {
    const newObservation: TranscriptObservation = {
      title: "",
      description: "",
      start_time: 0,
      end_time: 0,
      photo_ids: [],
    };

    const newObservations = [...transcriptData.observations];
    newObservations.splice(insertIndex, 0, newObservation);

    onTranscriptChange({
      ...transcriptData,
      observations: newObservations,
    });
  };

  const handleRemovePhoto = (observationIndex: number, photoId: string) => {
    onTranscriptChange({
      ...transcriptData,
      observations: transcriptData.observations.map((o, idx) =>
        idx === observationIndex
          ? { ...o, photo_ids: o.photo_ids.filter((id) => id !== photoId) }
          : o
      ),
    });
  };

  const handleMovePhotoUp = (observationIndex: number, photoId: string) => {
    const obs = transcriptData.observations[observationIndex];
    const photoIndex = obs.photo_ids.indexOf(photoId);

    if (photoIndex > 0) {
      const newPhotoIds = [...obs.photo_ids];
      [newPhotoIds[photoIndex - 1], newPhotoIds[photoIndex]] = [newPhotoIds[photoIndex], newPhotoIds[photoIndex - 1]];
      onTranscriptChange({
        ...transcriptData,
        observations: transcriptData.observations.map((o, idx) =>
          idx === observationIndex ? { ...o, photo_ids: newPhotoIds } : o
        ),
      });
    } else if (observationIndex > 0) {
      onTranscriptChange({
        ...transcriptData,
        observations: transcriptData.observations.map((o, idx) => {
          if (idx === observationIndex - 1) {
            return { ...o, photo_ids: [...o.photo_ids, photoId] };
          }
          if (idx === observationIndex) {
            return { ...o, photo_ids: o.photo_ids.filter(id => id !== photoId) };
          }
          return o;
        }),
      });
    }
  };

  const handleMovePhotoDown = (observationIndex: number, photoId: string) => {
    const obs = transcriptData.observations[observationIndex];
    const photoIndex = obs.photo_ids.indexOf(photoId);

    if (photoIndex < obs.photo_ids.length - 1) {
      const newPhotoIds = [...obs.photo_ids];
      [newPhotoIds[photoIndex], newPhotoIds[photoIndex + 1]] = [newPhotoIds[photoIndex + 1], newPhotoIds[photoIndex]];
      onTranscriptChange({
        ...transcriptData,
        observations: transcriptData.observations.map((o, idx) =>
          idx === observationIndex ? { ...o, photo_ids: newPhotoIds } : o
        ),
      });
    } else if (observationIndex < transcriptData.observations.length - 1) {
      onTranscriptChange({
        ...transcriptData,
        observations: transcriptData.observations.map((o, idx) => {
          if (idx === observationIndex) {
            return { ...o, photo_ids: o.photo_ids.filter(id => id !== photoId) };
          }
          if (idx === observationIndex + 1) {
            return { ...o, photo_ids: [photoId, ...o.photo_ids] };
          }
          return o;
        }),
      });
    }
  };

  const handleMergeWithPrevious = (index: number) => {
    if (index <= 0) return;

    const prevObs = transcriptData.observations[index - 1];
    const currentObs = transcriptData.observations[index];

    const mergedObs: TranscriptObservation = {
      title: prevObs.title || currentObs.title,
      description: [prevObs.description, currentObs.description].filter(Boolean).join('\n\n'),
      start_time: Math.min(prevObs.start_time, currentObs.start_time),
      end_time: Math.max(prevObs.end_time, currentObs.end_time),
      photo_ids: [...prevObs.photo_ids, ...currentObs.photo_ids],
    };

    const newObservations = transcriptData.observations.filter((_, idx) => idx !== index - 1 && idx !== index);
    newObservations.splice(index - 1, 0, mergedObs);

    onTranscriptChange({
      ...transcriptData,
      observations: newObservations,
    });
  };

  const handleAddPhotoToObservation = (obsIndex: number, photoId: string) => {
    onTranscriptChange({
      ...transcriptData,
      observations: transcriptData.observations.map((obs, idx) =>
        idx === obsIndex ? { ...obs, photo_ids: [...obs.photo_ids, photoId] } : obs
      ),
    });
  };

  const handleOpenPhotoManager = (obsIndex: number) => {
    setPhotoManagerObsIndex(obsIndex);
    setPhotoManagerOpen(true);
  };

  return (
    <div className={cn("kii-editor", className)}>
      <div className="kii-editor-save-status" data-testid="save-status">
        <span>
          {isSaving
            ? "Saving..."
            : lastSaved
              ? `Last saved at ${formatDate(lastSaved, "time")}`
              : "Not saved yet"}
          {hasChanges && !isSaving && " (Unsaved changes)"}
        </span>
        {onUndo && (
          <button
            onClick={onUndo}
            disabled={!canUndo}
            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            title="Undo last save (Ctrl+Z)"
          >
            <Icon name={iconNames.undo} size={14} />
            Undo
          </button>
        )}
      </div>

      {transcriptData.observations.map((observation, idx) => (
        <div key={idx} className="kii-editor-observation-group">
          <div className="kii-editor-insert-container">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleInsertObservation(idx)}
              className="kii-editor-insert-button"
              data-testid={`button-insert-${idx}`}
            >
              <Icon name={iconNames.add} size={14} className="mr-1" />
              Insert observation
            </Button>
          </div>

          <ObservationCard
            observation={observation}
            index={idx}
            totalObservations={transcriptData.observations.length}
            attachments={attachments}
            nmPhotos={nmPhotos}
            isNMDocument={isNMDocument}
            onUpdateTitle={handleUpdateTitle}
            onUpdateDescription={handleUpdateDescription}
            onDeleteObservation={handleDeleteObservation}
            onMoveObservationUp={handleMoveObservationUp}
            onMoveObservationDown={handleMoveObservationDown}
            onRemovePhoto={handleRemovePhoto}
            onMovePhotoUp={handleMovePhotoUp}
            onMovePhotoDown={handleMovePhotoDown}
            onMergeWithPrevious={handleMergeWithPrevious}
            onRotatePhoto={onRotatePhoto}
            pendingRotations={pendingRotations}
            onClearPendingRotation={onClearPendingRotation}
            processingPhotoIds={processingPhotoIds}
            onOpenPhotoManager={onPermanentDeletePhoto ? handleOpenPhotoManager : undefined}
          />

          {idx < transcriptData.observations.length - 1 && (
            <div className="kii-editor-merge-container">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleMergeWithPrevious(idx + 1)}
                className="kii-editor-observation-merge-button"
                title="Merge with previous"
                data-testid={`button-merge-${idx + 1}`}
              >
                <Icon name={iconNames.merge} size={16} />
              </Button>
            </div>
          )}
        </div>
      ))}

      <div className="kii-editor-insert-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleInsertObservation(transcriptData.observations.length)}
          className="kii-editor-insert-button"
          data-testid="button-insert-end"
        >
          <Icon name={iconNames.add} size={14} className="mr-1" />
          Insert observation
        </Button>
      </div>

      {isDebugEnabled && (
        <div className="kii-editor-debug">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <h3 className="text-sm font-semibold">Raw Transcript JSON</h3>
              </CardHeader>
              <CardContent>
                <pre className="kii-editor-debug-json">
                  {JSON.stringify(transcriptData, null, 2)}
                </pre>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <h3 className="text-sm font-semibold">State Information</h3>
              </CardHeader>
              <CardContent className="text-xs space-y-2">
                <div><strong>Has Changes:</strong> {hasChanges ? "Yes" : "No"}</div>
                <div><strong>Is Saving:</strong> {isSaving ? "Yes" : "No"}</div>
                <div><strong>Last Saved:</strong> {lastSaved ? formatDate(lastSaved, "datetime") : "Never"}</div>
                <div><strong>Observations:</strong> {transcriptData?.observations.length || 0}</div>
                <div><strong>Attachments:</strong> {attachments.length}</div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {onPermanentDeletePhoto && (
        <PhotoManagementDialog
          open={photoManagerOpen}
          onOpenChange={setPhotoManagerOpen}
          observationIndex={photoManagerObsIndex}
          observation={transcriptData.observations[photoManagerObsIndex] || { title: "", description: "", start_time: 0, end_time: 0, photo_ids: [] }}
          allPhotos={nmPhotos}
          allObservations={transcriptData.observations}
          isNMDocument={isNMDocument}
          onAddPhoto={handleAddPhotoToObservation}
          onPermanentDelete={onPermanentDeletePhoto}
          onUploadPhotos={onUploadPhotos || (async () => {})}
          uploadProgress={uploadProgress}
          isUploading={isUploadingPhotos}
        />
      )}
    </div>
  );
}
